/**
 * Where a new game lands (console spec §5.4): after the game before it, on the
 * same field. Add games ×N chains these, so three added games arrive spaced
 * on one field and the organizer edits by exception. Rest is between a team's
 * games, not a field's, so the gap is the game length alone.
 */
import type { Game } from "./types";

export type GameSlot = Pick<Game, "scheduledAt" | "venueId" | "field">;

/** The pool's latest scheduled game, or undefined when none has a time. */
export function latestGame<T extends GameSlot>(games: T[]): T | undefined {
  let latest: T | undefined;
  for (const g of games) {
    if (!g.scheduledAt || Number.isNaN(Date.parse(g.scheduledAt))) continue;
    if (!latest || Date.parse(g.scheduledAt) > Date.parse(latest.scheduledAt!)) latest = g;
  }
  return latest;
}

/** The slot after `previous`: its field, one game length later. Empty when there is no previous or it has no time. */
export function nextGameSlot(previous: GameSlot | undefined, gameLengthMinutes: number): GameSlot {
  if (!previous?.scheduledAt) return {};
  const ms = Date.parse(previous.scheduledAt);
  if (Number.isNaN(ms)) return {};
  return {
    scheduledAt: new Date(ms + gameLengthMinutes * 60_000).toISOString(),
    ...(previous.venueId ? { venueId: previous.venueId } : {}),
    ...(previous.field ? { field: previous.field } : {}),
  };
}
